import {
  Box,
  Button,
  Chip,
  Drawer,
  DrawerProps,
  FormControl,
  IconButton,
  Link,
  Select,
  Stack,
  styled,
  TextField,
  Typography,
} from "@mui/material"
import theme from "../../app/theme"
import { processScheduleStatus, UploadStatus } from "../../constants/data"

export const StyledFormControl = styled(FormControl)`
  flex: 1;

  & .MuiInputBase-root {
    border-left: 0;
    border-radius: 8px !important;
  }
`

export const StyledFormControlSearch = styled(FormControl)`
  flex: 1.5;
`

export const TransactionsContainer = styled(Stack)`
  display: flex;
  flex: 1;
  background: white;
  padding: 20px;
  position: relative;
`

export const StyledButton = styled(Button)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "14px",
  backgroundColor: "white",
  padding: "8px 12px",
  lineHeight: "22px",
  borderRadius: "8px",
  color: "#263238",
  border: "1px solid #d0d5dd",
  textTransform: "unset",
  boxShadow: "0px 1px 2px 0px #0000000d",
  [theme.breakpoints.down("sm")]: {
    flex: 1,
    fontSize: "12px",
  },
}))

export const ResetButton = styled(StyledButton)`
  height: 40px;
  border-radius: 4px;
  padding: 8px 12px;
`

export const StyledDrawer = styled(Drawer)<DrawerProps>(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  "& .MuiDrawer-paper": {
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      width: "550px",
    },
  },
}))

export const DrawerHeader = styled("div")`
  display: flex;
  justify-content: space-between;
  background-color: rgba(235, 239, 243, 0.8);
  height: 60px;
  align-items: center;
  padding-left: 20px;
`

export const DrawerTitle = styled(Typography)`
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: rgba(53, 56, 71, 1);
`

export const DrawerContent = styled(Box)(() => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: "10px",
  padding: "  0 24px 20px 24px",
  backgroundColor: "white",
  overflow: "auto",
}))

export const ReconciliationFormContainer = styled("div")`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

export const CloseIconButton = styled(IconButton)`
  position: absolute;
  top: 8px;
  right: 8px;
`

export const DrawerDataRow = styled("div")`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  @media (max-width: ${theme.breakpoints.values["xs"]}px) {
    flex-direction: column;
  }
`

export const DrawerDataHeading = styled(Typography)`
  font-size: 14px;
  font-weight: 700;
  width: 150px;
  color: rgba(38, 50, 56, 1);
`

export const DrawerData = styled(Typography)`
  font-size: 14px;
  font-weight: 400;
  flex: 1;
  color: rgba(38, 50, 56, 1);
`

export const ReconcileIconButton = styled(IconButton)`
  border: 1px solid #d8dbdf;
  border-radius: 4px;
  font-size: 14px;
  color: #263238;
`
export const SyncIconButton = styled(IconButton)`
  border-radius: 4px;
  font-size: 14px;
  color: #263238;
`

export const MarkedPendingChip = styled(Chip)`
  font-size: 12px;
  font-weight: 500;
  padding-left: 8px;
  border: 0.5px solid #c596031a;
  color: #c59603;
  background: #fff3cc;
  svg {
    height: 6px;
    width: 6px;
    fill: #c59603;
  }
`

export const MarkedYesChip = styled(MarkedPendingChip)`
  border-color: #027a481a;
  color: #027a48;
  background: #ecfdf3;
  svg {
    fill: #027a48;
  }
`
export const MarkedUploadedChip = styled(MarkedPendingChip)`
  border-color: #007bff;
  color: #007bff;
  background: #e2f3ff;
  svg {
    fill: #007bff;
  }
`

export const MarkedNoChip = styled(MarkedPendingChip)`
  border-color: #e334291a;
  color: #e33429;
  background: #e334291a;
  svg {
    fill: #e33429;
  }
`

export const ReferenceNoCell = styled(Box)`
  color: #3753e9;
  text-decoration: underline;
  cursor: pointer;
`

interface ExternalLinkProps {
  value?: string
}

export const ExternalLink = styled(Link)<ExternalLinkProps>`
  color: ${({ value }) => (value ? "#3753e9" : "#263238")};
  text-decoration: ${({ value }) => (value ? "underline" : "none")};
  margin-right: 12px;
  cursor: ${({ value }) => (value ? "pointer" : "default")};
`

export const ReconciliationMessage = styled(Box)`
  font-size: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px 18px;
  border-radius: 8px;
  gap: 2px;
  background: rgba(235, 239, 243, 1);
`

export const ReconciliationMessageText = styled(Box)`
  font-size: 12px;
  font-weight: 500;
`

export const StyledFilterDrawer = styled(Drawer)(() => ({
  "& .MuiPaper-root": {
    width: "300px",
    border: "none",
    borderRadius: "20px 0px 0px 20px",
    position: "absolute",
    zIndex: 100,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
}))

interface ContentWrapperProps {
  isOpen: boolean
  drawerWidth: number
}

export const ContentWrapper = styled(Box)<ContentWrapperProps>(
  ({ isOpen, drawerWidth }) => ({
    flex: 1,
    transition: "all 0.3s ease",
    width: isOpen ? `calc(100% - ${drawerWidth}px)` : "100%",
    marginRight: isOpen ? `${drawerWidth}px` : 0,
  }),
)

export const StyledCircle = styled("div")`
  width: 12px;
  height: 12px;
  background-color: #ff5630;
  line-height: 12px;
  border-radius: 1rem;
  color: #ffff;
  text-align: center;
  font-size: 12px !important;
`

export const ApprovalsButtonsContainer = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  button {
    height: 40px;
  }
  & .checkbox-container {
    display: flex;
    flex-wrap: wrap;
  }

  > div:only-child {
    margin-left: auto;
  }

  @media (max-width: 500px) {
    & .download-refresh-buttons-container {
      flex-grow: 1;
      display: flex;
      justify-content: flex-end;
      align-items: center;
      margin-left: auto;
    }
    & .checkbox-container {
      flex-grow: 1;
      justify-content: space-between;
      margin-bottom: 20px;
      align-items: center;
    }
  }
`

interface InputProps {
  updateCategory?: boolean
}

export const StyledCategoryFormControl = styled(FormControl)<InputProps>`
  flex: 1;
  width: ${({ updateCategory }) => (updateCategory ? "100%" : "120px")};
  @media only screen and (max-width: 480px) {
    width: 100%;
  }
`

export const StyledTransactionsContainer = styled("div")<InputProps>`
  display: flex;
  flex-direction: ${({ updateCategory }) =>
    updateCategory ? "column" : "row"};
  justify-content: ${({ updateCategory }) =>
    updateCategory ? "space-between" : "flex-start"};
  align-items: center;
  gap: 10px;

  @media only screen and (max-width: 480px) {
    flex-direction: column;
    align-items: ${({ updateCategory }) =>
      updateCategory ? "center" : "stretch"};
    gap: 10px;
  }
`
export const StyledCategorySelectorContainer = styled("div")<InputProps>`
  display: flex;
  flex-direction: ${({ updateCategory }) =>
    updateCategory ? "column" : "row"};
  justify-content: center;
  align-items: center;
  gap: ${({ updateCategory }) => (updateCategory ? "30px" : "10px")};
  padding: ${({ updateCategory }) => updateCategory && "20px"};
  width: ${({ updateCategory }) => updateCategory && "100%"};

  @media only screen and (max-width: 480px) {
    flex-direction: column;
    align-items: ${({ updateCategory }) =>
      updateCategory ? "center" : "stretch"};
  }
`

export const StyledCategoryButtonContainer = styled("div")<InputProps>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  gap: 10px;
  width: 95%;

  @media only screen and (max-width: 480px) {
    flex-direction: row;
    align-items: center;
    gap: 10px;
  }
`

export const StyledTransactionInfo = styled("div")`
  display: flex;
  height: 100%;
  flex-direction: column;
  justify-content: space-between;
`

export const StyledChip = styled(Chip)`
  margin: 2px;
  height: 25px;
  max-width: 100px;

  & .MuiChip-label {
    font-size: 10px;
    padding-inline: 3px;
    cursor: default;
  }
`

export const StyledSelect = styled(Select)(() => ({
  minWidth: "80px",
  borderRadius: "8px  0  0 8px !important",
}))

export const StyledTextField = styled(TextField)(() => ({
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
  "& .MuiInputBase-root": {
    borderLeft: 0,
    borderRadius: "0 8px 8px 0 !important",
  },
}))

export const ChipContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  width: "fit-content",
  position: "relative",
}))

interface StyledChipProps {
  isExpanded: boolean
  customColor: string
}

export const ExpandableChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "isExpanded",
})<StyledChipProps>(({ isExpanded, customColor }) => ({
  transition: "all 0.3s ease",
  cursor: "pointer",
  zIndex: isExpanded ? 10 : 1,
  maxWidth: isExpanded ? "200px" : "40px",
  border: "2px solid #fff",
  marginInlineEnd: "2px",
  margin: "-6px",
  backgroundColor: customColor,
  "&:hover": {
    backgroundColor: customColor,
  },
  "& .MuiChip-label": {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    display: isExpanded ? "block" : "none",
    fontSize: "13px",
    fontWeight: "600",
    paddingInline: "7px",
    color: "#fff",
  },
  "& .MuiChip-avatar": {
    display: isExpanded ? "none" : "flex",
    backgroundColor: "transparent",
    fontSize: "14px",
    margin: 2,
    fontWeight: "600",
    alignItems: "center",
    justifyContent: "center",
    paddingInline: "2px",
    color: "#fff",
  },
}))

interface StyledCancelReconcileButtonProps {
  scheduleStatus?: string
}

export const StyledCancelReconcileButton = styled(
  StyledButton,
)<StyledCancelReconcileButtonProps>(({ theme, scheduleStatus }) => ({
  backgroundColor:
    scheduleStatus === processScheduleStatus.IN_PROGRESS
      ? "#edf7ee"
      : "#fff7ed",
  color:
    scheduleStatus === processScheduleStatus.IN_PROGRESS
      ? theme.palette.success.dark
      : theme.palette.error.dark,
  border: `1px solid ${
    scheduleStatus === processScheduleStatus.IN_PROGRESS
      ? theme.palette.success.light
      : theme.palette.error.light
  }`,
}))

export const PrimaryButton = styled(StyledButton)`
  background-color: #ffc100;
  border: none;
`

export const StyledScheduleBox = styled(Box)(({ theme }) => ({
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "24px",
  backgroundColor: "#fff7ed",
  padding: "12px",
  margin: " 16px 0px",
  borderRadius: "8px",
  color: "#9f2d00",
  border: "1px solid #ffd7a8",
  textTransform: "unset",
  boxShadow: "0px 1px 2px 0px #0000000d",
  justifyContent: "center",
  [theme.breakpoints.down("sm")]: {
    flex: 1,
    fontSize: "12px",
  },

  span: { fontWeight: 800 },
}))

export const ReconcilePrimaryButton = styled(PrimaryButton)`
  width: 100%;
  gap: 8px;
  margin-top: 12px;
`

export const TransactionsFilterWrapper = styled(Box)(() => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: "10px",
  marginBottom: "16px",

  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "stretch",
  },
}))

export const StyledTypeFormControl = styled(FormControl)`
  & .MuiInputBase-root {
    border-left: 0;
    border-radius: 8px !important;
  }
`

export const ReconciliationTimeMessage = styled(Box)(() => ({
  display: "flex",
  padding: "12px 16px",
  backgroundColor: "#eff6ff",
  border: `1px solid #bedbff`,
  borderRadius: "8px",
  color: "#193cb8",
  fontSize: "14px",
}))

export const StyledBox = styled(Box)(() => ({
  justifyContent: "space-between",
  gap: 2,
  alignItems: "flex-start",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: 0,
  },
}))
export const HeaderActionStack = styled(Stack)(() => ({
  display: "flex",
  spacing: 1,
  flexDirection: "row",
  alignItems: "center",
  gap: 10,

  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "stretch",
  },
}))

interface BankUploadMessageProps {
  status?: string
}

const statusStyles: {
  [status: string]: {
    backgroundColor: string
    border: string
  }
  default: {
    backgroundColor: string
    border: string
  }
} = {
  [UploadStatus.SUCCESS]: {
    backgroundColor: "#e2f3ff",
    border: "1px solid #007bff",
  },
  [UploadStatus.FAILED]: {
    backgroundColor: "#e334291a",
    border: "1px solid #e334291a",
  },
  [UploadStatus.PROCESSING]: {
    backgroundColor: "#fefce8",
    border: "1px solid #fff085",
  },
  [UploadStatus.RECONCILED]: {
    backgroundColor: "#ecfdf3",
    border: "1px solid #027a481a",
  },
  default: {
    backgroundColor: "#fefce8",
    border: "1px solid #fff085",
  },
}

export const BankUploadMessage = styled(Box)<BankUploadMessageProps>(
  ({ status }) => ({
    padding: "12px 16px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "24px",
    textTransform: "unset",
    minHeight: "80px",
    alignContent: "center",
    width: "460px",
    span: { fontWeight: 700 },

    ...statusStyles[status || "default"],

    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  }),
)

interface UploadBoxProps {
  isDragging: boolean
  selectedBank: boolean
}

export const UploadBox = styled(Box)<UploadBoxProps>(
  ({ isDragging, selectedBank }) => ({
    border: "2px dashed",
    borderColor: isDragging ? "primary.main" : "#d1d5dc",
    borderRadius: 4,
    padding: 4,
    textAlign: "center",
    backgroundColor: isDragging ? "action.hover" : "transparent",
    transition: "all 0.2s ease",
    cursor: selectedBank ? "pointer" : "not-allowed",
    opacity: !selectedBank ? 0.5 : 1,
  }),
)

export const UploadAreaBox = styled(Box)(() => ({
  width: 80,
  height: 80,
  borderRadius: "50%",
  backgroundColor: "#FFF4E0",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto 16px",
}))

export const StyledSearchBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flex: 1,
  gap: 0,
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    alignItems: "stretch",
    gap: 0,
  },
}))

export const OutflowContentBox = styled(Box)({
  padding: "12px 16px",
  borderRadius: "12px",
  backgroundColor: "#fefce8",
  border: "1px solid #fff085",
  display: "flex",
  flexDirection: "column",
  gap: "12px",

  [theme.breakpoints.down("xs")]: {
    width: "100%",
  },
})

export const OutflowTitle = styled(Typography)({
  fontSize: "13px",
  fontWeight: 800,
  letterSpacing: "0.06em",
  color: "#78350f",
  textTransform: "uppercase",
})

export const OutflowMetaRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  gap: "8px",
})

export const OutflowMetaLabel = styled(Typography)({
  fontSize: "14px",
  fontWeight: 700,
  color: "#78350f",
})

export const OutflowMetaValue = styled(Typography)({
  fontSize: "14px",
  fontWeight: 600,
  color: "#2563eb",
  textAlign: "right",
  flex: 1,
})

export const OutflowDivider = styled(Box)({
  borderTop: "1.5px dashed #ffd985",
  margin: "4px 0",
})

export const OutflowSectionCard = styled(Box)({
  backgroundColor: "#ffffff",
  borderRadius: "10px",
  padding: "12px 14px",
  display: "flex",
  flexDirection: "column",
  gap: "10px",
})

export const OutflowSectionTitle = styled(Typography)({
  fontSize: "11px",
  fontWeight: 700,
  letterSpacing: "0.08em",
  color: "#6b7280",
  textTransform: "uppercase",
})

export const OutflowChipsRow = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
})

interface OutflowLinkChipProps {
  isClickable: boolean
}

export const OutflowLinkChip = styled(Box)<OutflowLinkChipProps>(
  ({ isClickable }) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "5px 12px",
    backgroundColor: "#f3f4f6",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: 500,
    color: "#374151",
    cursor: isClickable ? "pointer" : "not-allowed",
    transition: "background 0.15s",
    "&:hover": {
      backgroundColor: isClickable ? "#e5e7eb" : "transparent",
    },
  }),
)
